import { useState, useCallback, useEffect } from "react";
import { useAtomValue } from "jotai";
import { SelectedFundAtom } from "@/store/FundStateAtom";
import { SelectedReportingPeriodAtom } from "@/store/ReportingPeriodStateAtom";
import { ReportingPeriod } from "@/types/ReportingPeriod";
import { toast } from "sonner";
import { Report, ReportType, ReportPreviewData, ExceptionDetailRow, ExceptionSummary } from "./types";
import { formatFullTimestamp } from "./utils/format";
import { getHoldings } from "@/api/Holdings";
import { getTransactions } from "@/api/Transactions";
import { getRuleExceptions } from "@/api/RuleExceptions";
import { getValidationRulesForFund } from "@/api/Validations";
import { HoldingWithInstrumentAndAccount, TransactionWithInstrumentAndAccount } from "@/types";
import { generatePDFReport } from "./utils/pdfGenerator";
import { generateCSVReport } from "./utils/csvGenerator";

export function useReportsController() {
  const selectedFund = useAtomValue(SelectedFundAtom);
  const selectedReportingPeriod = useAtomValue(SelectedReportingPeriodAtom);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportHistory, setReportHistory] = useState<Report[]>([]);
  const [holdings, setHoldings] = useState<HoldingWithInstrumentAndAccount[]>([]);
  const [transactions, setTransactions] = useState<TransactionWithInstrumentAndAccount[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [ruleExceptions, setRuleExceptions] = useState<any[]>([]);
  const [rulesRunCount, setRulesRunCount] = useState<number>(0);

  // Fetch real data from API
  const fetchReportData = useCallback(async () => {
    if (!selectedFund || !selectedReportingPeriod) {
      setHoldings([]);
      setTransactions([]);
      return;
    }

    setIsLoadingData(true);
    try {
      const [holdingsData, transactionsData, exceptionsData, validationRules] = await Promise.all([
        getHoldings(selectedFund.fundId, parseInt(selectedReportingPeriod.reportingPeriodId)),
        getTransactions(selectedFund.fundId, parseInt(selectedReportingPeriod.reportingPeriodId)),
        getRuleExceptions(parseInt(selectedReportingPeriod.reportingPeriodId)),
        getValidationRulesForFund(Number(selectedFund.fundId)),
      ]);

      setHoldings(holdingsData);
      setTransactions(transactionsData);
      setRuleExceptions(exceptionsData || []);
      setRulesRunCount((validationRules || []).length);
    } catch (error) {
      console.error("Error fetching report data:", error);
      toast.error("Failed to fetch report data");
    } finally {
      setIsLoadingData(false);
    }
  }, [selectedFund, selectedReportingPeriod]);

  // Fetch data when fund or reporting period changes
  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  // Get report preview data from real data
  const getReportPreviewData = useCallback((): ReportPreviewData | null => {
    if (!selectedFund || !selectedReportingPeriod) {
      return null;
    }

    const asOfDate = formatFullTimestamp();

    // Calculate NAV summary from holdings
    const confirmedHoldings = holdings.filter(h => h.isConfirmed);
    const unconfirmedHoldings = holdings.filter(h => !h.isConfirmed);
    const totalNAV = holdings.reduce((sum, holding) => sum + holding.baseAmount, 0);

    // Get latest transaction date
    const latestTransaction = transactions
      .sort((a, b) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime())[0];

    // Exception summary and details
    const failures = ruleExceptions.length;
    const failuresOpen = ruleExceptions.filter(e => ["New", "Open", "In Progress"].includes((e.status || "").trim())).length;
    const failuresResolved = ruleExceptions.filter(e => ["Resolved", "Waived", "Closed"].includes((e.status || "").trim())).length;
    const rulesRun = rulesRunCount;
    const passes = Math.max(0, rulesRun - failures);

    const exceptionSummary: ExceptionSummary = {
      rulesRun,
      passes,
      failures,
      failuresOpen,
      failuresResolved,
    };

    const exceptionDetails: ExceptionDetailRow[] = (ruleExceptions || []).map((e: any) => ({
      rule: e.ruleKey,
      description: e.description || e.shortDescription,
      result: undefined,
      expected: undefined,
      tolerance: undefined,
      assignedTo: e.assignedTo,
      checkedBy: undefined,
      notes: e.notes,
      status: e.status,
    }));

    return {
      fundName: selectedFund.name,
      fundKey: selectedFund.fundKey || selectedFund.name,
      reportingPeriod: new Date(selectedReportingPeriod.period).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
      asOfDate,
      navSummary: {
        totalNAV,
        currency: "USD", // Default currency, could be enhanced to detect from data
        totalHoldings: holdings.length,
        confirmedHoldings: confirmedHoldings.length,
        unconfirmedHoldings: unconfirmedHoldings.length,
        asOfDate,
      },
      totalTransactions: transactions.length,
      totalHoldings: holdings.length,
      lastTransactionDate: latestTransaction 
        ? new Date(latestTransaction.transactionDate).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : undefined,
      exceptionSummary,
      exceptionDetails,
    };
  }, [selectedFund, selectedReportingPeriod, holdings, transactions]);

  // Generate a new report with actual PDF generation
  const generateReport = useCallback(
    async (
      reportType: ReportType,
      format: "PDF" | "CSV" = "PDF"
    ) => {
      if (!selectedReportingPeriod || !selectedFund) {
        toast.error("Please select a fund and reporting period");
        return;
      }

      const reportPreviewData = getReportPreviewData();
      if (!reportPreviewData) {
        toast.error("Failed to prepare report data");
        return;
      }

      setIsGeneratingReport(true);

      try {
        // Build common report options
        const reportOptions = {
          includeNAVSummary:
            reportType === ReportType.NAV_SUMMARY ||
            reportType === ReportType.COMBINED_REPORT,
          includeHoldings:
            reportType === ReportType.HOLDINGS_DETAIL ||
            reportType === ReportType.COMBINED_REPORT,
          includePerformance: false, // Not implemented yet
          format,
        } as const;

        let blob: Blob;
        let fileExtension: string;

        if (format === "PDF") {
          blob = await generatePDFReport({
            reportData: reportPreviewData,
            reportOptions,
            reportType,
            holdings,
            transactions,
          });
          fileExtension = "pdf";
        } else {
          // CSV generation
          blob = generateCSVReport({
            reportData: reportPreviewData,
            reportOptions,
            reportType,
            holdings,
            transactions,
          });
          fileExtension = "csv";
        }

        // Create download URL
        const url = URL.createObjectURL(blob);
        
        // Create report record
        const now = new Date();
        const reportId = `report-${Date.now()}`;
        const periodName = new Date(selectedReportingPeriod.period).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });

        const newReport: Report = {
          reportId,
          fundId: selectedFund.fundId.toString(),
          periodId: selectedReportingPeriod.reportingPeriodId,
          periodName,
          name: `${periodName} ${reportType}`,
          type: reportType,
          format,
          createdAt: now.toISOString(),
          createdBy: "Current User",
          fileSize: blob.size,
          downloadUrl: url,
        };

        // Add to report history
        setReportHistory((prev) => [newReport, ...prev]);

        // Auto-download the generated PDF
        const a = document.createElement("a");
        a.href = url;
        a.download = `${newReport.name}.${fileExtension}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        toast.success(`${format} report generated and downloaded successfully`);
      } catch (error) {
        console.error(`Error generating ${format} report:`, error);
        toast.error(`Failed to generate ${format} report`);
      } finally {
        setIsGeneratingReport(false);
      }
    },
    [selectedFund, selectedReportingPeriod, holdings, transactions, getReportPreviewData]
  );

  // Download a previously generated report
  const downloadReport = useCallback(
    async (reportId: string) => {
      const report = reportHistory.find((r) => r.reportId === reportId);
      if (!report) {
        toast.error("Report not found");
        return;
      }

      if (report.downloadUrl) {
        // If we have a stored URL, use it
        const a = document.createElement("a");
        a.href = report.downloadUrl;
        a.download = `${report.name}.${report.format.toLowerCase()}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast.success(`Downloaded ${report.name}`);
      } else {
        // Regenerate the report if the blob is not cached
        const reportPreviewData = getReportPreviewData();
        if (!reportPreviewData) {
          toast.error("Cannot regenerate report - data not available");
          return;
        }

        try {
          let regeneratedBlob: Blob;
          let fileExtension: string;

          const commonOptions = {
            includeNAVSummary:
              report.type === ReportType.NAV_SUMMARY ||
              report.type === ReportType.COMBINED_REPORT,
            includeHoldings:
              report.type === ReportType.HOLDINGS_DETAIL ||
              report.type === ReportType.COMBINED_REPORT,
            includePerformance: false,
            format: report.format,
          } as const;

          if (report.format === "PDF") {
            regeneratedBlob = await generatePDFReport({
              reportData: reportPreviewData,
              reportOptions: commonOptions,
              reportType: report.type,
              holdings,
              transactions,
            });
            fileExtension = "pdf";
          } else {
            regeneratedBlob = generateCSVReport({
              reportData: reportPreviewData,
              reportOptions: commonOptions,
              reportType: report.type,
              holdings,
              transactions,
            });
            fileExtension = "csv";
          }

          const url = URL.createObjectURL(regeneratedBlob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `${report.name}.${fileExtension}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);

          toast.success(`Downloaded ${report.name}`);
        } catch (error) {
          console.error("Error regenerating report:", error);
          toast.error("Failed to download report");
        }
      }
    },
    [reportHistory, selectedFund, holdings, transactions, getReportPreviewData]
  );

  return {
    isGeneratingReport,
    isLoadingData,
    selectedReportingPeriod,
    generateReport,
    reportHistory,
    downloadReport,
    getReportPreviewData,
    holdings,
    transactions,
    refreshData: fetchReportData,
  };
}
